import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FolderKanban, Award, MessageSquare, Sparkles, Download } from "lucide-react";
import AiInsights from "@/components/schools/AiInsights";
import DemoFlow from "@/components/schools/DemoFlow";
import SchoolKpi from "@/components/schools/SchoolKpi";
import SchoolFilters, { useSchoolFilters } from "@/components/schools/SchoolFilters";
import { useDynamicInsights } from "@/hooks/useDynamicInsights";
import { portfolioSchoolStats, portfolioSpotlight, aiInsightsPortfolio } from "@/data/greenwoodPersonas";
import { downloadCsv } from "@/lib/csv";

// Peer-school benchmarks — comparable IB/international portfolios programme.
const portfolioBenchmarks = {
  activePortfolios: 820,
  avgArtifacts: 5.4,
  badgesIssued: 3100,
  reflectionsLogged: 9200,
} as const;

export default function PortfolioDashboard() {
  const [f] = useSchoolFilters();
  const scopedSpotlight = portfolioSpotlight.filter((p) =>
    f.grade === "all" ? true : p.grade.startsWith(f.grade),
  );
  const { insights, contextLabel } = useDynamicInsights(aiInsightsPortfolio, "portfolio");
  const exportPortfolio = () => {
    const rows: (string | number)[][] = [
      ["WeKIT Education Intelligence™ — Portfolio snapshot"],
      ["Generated", new Date().toISOString()],
      [],
      ["Metric", "Value", "Peer benchmark"],
      ["Active portfolios", portfolioSchoolStats.activePortfolios, portfolioBenchmarks.activePortfolios],
      ["Avg. artifacts / student", portfolioSchoolStats.avgArtifacts, portfolioBenchmarks.avgArtifacts],
      ["Badges issued", portfolioSchoolStats.badgesIssued, portfolioBenchmarks.badgesIssued],
      ["Reflections logged", portfolioSchoolStats.reflectionsLogged, portfolioBenchmarks.reflectionsLogged],
      [],
      ["Portfolio spotlight"],
      ["Student", "Grade", "Artifacts", "Focus"],
      ...portfolioSpotlight.map((p) => [p.name, p.grade, p.artifacts, p.focus]),
    ];
    downloadCsv(`greenwood-portfolio-snapshot-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  return (
    <>
      <PageHead title="Portfolio Dashboard | WeKIT Education Intelligence™" description="School-wide digital portfolios" path="/schools/portfolio" />
      <div className="container max-w-7xl py-8 space-y-10">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-2">
              <Sparkles className="w-3.5 h-3.5 text-primary" /> Portfolio Dashboard
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Digital learner profiles</h1>
            <p className="text-muted-foreground mt-1">University-ready evidence across the school</p>
          </div>
          <Button size="sm" variant="outline" onClick={exportPortfolio}>
            <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
          </Button>
        </header>

        <SchoolFilters />

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SchoolKpi icon={<FolderKanban className="w-4 h-4" />}   label="Active portfolios"        value={portfolioSchoolStats.activePortfolios.toLocaleString()}                                 tone="positive" series={[820, 890, 940, 985, 1010, 1042, 1064]}          benchmark={portfolioBenchmarks.activePortfolios} />
          <SchoolKpi                                              label="Avg. artifacts / student" value={portfolioSchoolStats.avgArtifacts.toFixed(1)} hint="+1.4 vs last term"                    tone="positive" series={[4.2, 4.8, 5.3, 5.9, 6.4, 6.9, 7.2]}             benchmark={portfolioBenchmarks.avgArtifacts} />
          <SchoolKpi icon={<Award className="w-4 h-4" />}          label="Badges issued"            value={portfolioSchoolStats.badgesIssued.toLocaleString()}                                     tone="positive" series={[2100, 2540, 2980, 3320, 3680, 4050, 4382]}      benchmark={portfolioBenchmarks.badgesIssued} />
          <SchoolKpi icon={<MessageSquare className="w-4 h-4" />}  label="Reflections logged"       value={portfolioSchoolStats.reflectionsLogged.toLocaleString()}                                tone="positive" series={[6800, 7900, 9000, 10200, 11300, 12100, 12904]}  benchmark={portfolioBenchmarks.reflectionsLogged} />
        </section>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Portfolio spotlight</CardTitle>
            <p className="text-xs text-muted-foreground">
              {f.grade === "all"
                ? "Standout learners by evidence depth"
                : `Grade ${f.grade} learners · ${scopedSpotlight.length} in view`}
            </p>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {scopedSpotlight.length === 0 ? (
              <div className="col-span-full rounded-xl border border-dashed p-6 text-sm text-muted-foreground text-center">
                No spotlight portfolios in this grade yet. Try another filter.
              </div>
            ) : (
              scopedSpotlight.map((p) => (
                <div key={p.name} className="rounded-xl border p-4 space-y-2">
                  <div className="text-sm font-semibold">{p.name}</div>
                  <div className="text-xs text-muted-foreground">Grade {p.grade}</div>
                  <Badge variant="secondary" className="rounded-full text-[10px]">{p.focus}</Badge>
                  <div className="text-xs text-muted-foreground pt-1">{p.artifacts} artifacts</div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <AiInsights insights={insights} contextLabel={contextLabel} title="Portfolio insights" subtitle="Signals from active learner portfolios" />
        <DemoFlow />
      </div>
    </>
  );
}
